import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import { PropId } from "building-types/props/propIds";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { PorchSettings } from "building-types/settings/porchSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { SettingsType } from "building-types/settings/settingsType";
import { EaveType, FasciaTopTrimSize } from "building-types/settings/settingsTypesEnum";
import { defaultExteriorPropCatalog } from "../elements/propCatalog/exteriorPropCatalog";
import { fasciaTopTrimSizeToValue } from "../fasciaTopTrimSizeToValue";
import { BuiltinHomeSettingsId, builtinHomeSettingsPrettyName } from "./builtinHomeSettingsId";
import {
  defaultFriezeBoardSettings,
  defaultHomeSettings,
  defaultPorchSettings,
  defaultWainscotingSettings,
} from "./defaultSettings";

const craftRoofSettings: RoofSettings = {
  type: SettingsType.ROOF_SETTINGS,
  eaveOverhang: 12,
  rakeOverhang: 12,
  shedOverhang: 12,
  eaveType: EaveType.BOXED_BOX,
  eaveReturnDistance: 4,
  eaveExtensionAtCoplanarRake: 4,
  eaveExtensionAtNonCoplanarRake: 0,
  eaveExtensionAtCoplanarWall: 1.75,
  rakeExtensionAtCoplanarWall: 0,
  includeFasciaTopTrim: true,
  fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
  eaveFasciaSize: 8,
  rakeFasciaSize: 8,
  rakeFriezeBoard: defaultFriezeBoardSettings,
  includeFriezeBoardOnPorches: true,
  roofFramingSpacing: 24,
  lookoutRafterSpacing: 24,
  includeFlyRafters: true,
  includeLookoutRafters: true,
  includeStickFramingAtStart: true,
  webChordSpacingAtStart: 16,
  includeStickFramingAtEnd: true,
  webChordSpacingAtEnd: 16,
  setBackForStepDownTruss: 72,
  generateRoofFraming: false,
};

const defaultCraftPorchSettings: PorchSettings = {
  ...defaultPorchSettings,
  maxColumnSpacing: 360,
  columnId: undefined,
};

const craftMaterials: { [id: string]: HomeSettings } = {
  [BuiltinHomeSettingsId.Craft_M2P]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Craft_M2P],
    exteriorMaterials: {
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      id: BuiltinHomeSettingsId.Craft_M2P + "_exterior",
      palettes: [BuiltinHomeSettingsId.Craft_M2P],
    },
    exteriorProps: {
      type: SettingsType.EXTERIOR_PROP_SETTINGS,
      propTypes: {
        ...defaultExteriorPropCatalog,
        garageDoorSingle: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
          propAssetId: PropId.DoorGar_S8x7_4x4Pattern_01,
        },
        garageDoorDouble: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
          propAssetId: PropId.DoorGar_D16x7_4x4Pattern_01,
        },
        doorSingleGlass: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
          propAssetId: PropId.DoorExt_Single36_Quarter1_02,
        },
        doorSingleGlassLow: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
          propAssetId: PropId.DoorExt_Single36_Quarter1_01,
        },
      },
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 0,
    },
    porch: defaultCraftPorchSettings,
    roof: craftRoofSettings,
    dormer: {
      ...defaultHomeSettings.dormer,
      eaveReturnDistance: 4,
    },
  },
};

export default craftMaterials;
